import { HStack, Input, TertiaryButton } from '@enechain/ecloud-designsystem'

import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { RequiredMark } from '~/components/common/RequiredMark'

/** プリセットボタン 1 つ (押すと入力値をこの `value` に置き換える)。 */
export type NumberPreset = {
  /** 入力欄へ入れる値 (= 文字列 state のまま扱う)。 */
  value: string
  /** ボタンの表示文言 (単位付き。 例: `80%` / `10 MW`)。 */
  label: string
}

type Props = {
  /** 項目ラベル (必須マーク付きで描く)。 */
  label: string
  presets: readonly NumberPreset[]
  /** 直接入力欄の値 (文字列のまま扱う = 入力途中の "0." 等を壊さない)。 */
  value: string
  onChange: (next: string) => void
  /**
   * 直接入力欄をラベルと `htmlFor` で紐付ける id。 同名の別 control がある項目
   * (= 「数量の決め方」 のセグメント名と重なる目標カバー率) は id を渡さず
   * [[inputAriaLabel]] で名前を付ける。
   */
  inputId?: string
  inputAriaLabel?: string
  /** 直接入力欄の幅 (design 実測値)。 */
  inputWidth: string
  min: number
  max: number
  step: number
}

/**
 * 「プリセットボタン群 + 直接入力」 の数値入力項目
 * (design `spot.html` `#bblk-pct-preset-group` / `#bblk-fixedmw-preset`)。
 *
 * 一括作成の 目標カバー率(%) と 1 コマあたり数量(MW) が同じ形なので、 design 由来の
 * レイアウト規約をここに 1 つだけ持つ:
 *
 * - プリセットは `TertiaryButton size="sm"` (書体・padding は sm) に design の
 *   inline `height:40px` を当てて隣の入力欄 (40px) と高さを揃える。
 * - 直接入力は `marginLeft={2}` を足し、 HStack の gap 8px と合わせて 16px 空けて
 *   プリセット群と視覚的に分ける。
 */
export const PresetNumberField = ({
  label,
  presets,
  value,
  onChange,
  inputId,
  inputAriaLabel,
  inputWidth,
  min,
  max,
  step,
}: Props): React.ReactElement => (
  <FieldRoot>
    <FieldLabel htmlFor={inputId}>
      {label}
      <RequiredMark />
    </FieldLabel>
    <HStack flexWrap="wrap" gap={2}>
      {presets.map((preset) => (
        <TertiaryButton
          key={preset.value}
          height="40px"
          onClick={() => onChange(preset.value)}
          size="sm"
        >
          {preset.label}
        </TertiaryButton>
      ))}
      <Input
        width={inputWidth}
        marginLeft={2}
        aria-label={inputAriaLabel}
        id={inputId}
        max={max}
        min={min}
        onChange={(e) => onChange(e.target.value)}
        step={step}
        type="number"
        value={value}
      />
    </HStack>
  </FieldRoot>
)
